import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, ChevronRight, ChevronLeft, CheckCircle, XCircle,
  Play, Pause, Volume2, VolumeX, Download, BookOpen, Clock,
  Loader2, Award, HelpCircle, RefreshCw, Copy, Check, Sparkles,
  Lightbulb, Share2
} from 'lucide-react';
import ShareButtons from '../components/ShareButtons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CopyBlock = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative my-4">
      <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 pr-24 overflow-x-auto whitespace-pre-wrap break-words">
        <code>{text}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-all bg-slate-700 hover:bg-primary text-slate-200 hover:text-white"
        data-testid="copy-prompt-btn"
      >
        {copied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy Prompt</>}
      </button>
    </div>
  );
};

const AcademyLesson = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);

  // Quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [quizResults, setQuizResults] = useState(null);

  useEffect(() => {
    // Reset all state when lesson changes
    setLoading(true);
    setAudioUrl(null);
    setAudioLoading(false);
    setIsPlaying(false);
    setIsMuted(false);
    setShowQuiz(false);
    setQuizAnswers({});
    setQuizResults(null);
    window.scrollTo(0, 0);
    fetchLesson();
  }, [lessonId]);

  const fetchLesson = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/academy/lesson/${lessonId}`, { headers });
      setLesson(response.data);
      // Record streak activity
      if (token) {
        axios.post(`${API}/academy/streak/record`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      }
    } catch (error) {
      console.error('Failed to fetch lesson:', error);
      if (error.response?.status === 403) {
        toast.error('Membership required to access this lesson');
        navigate('/academy/membership');
      } else {
        toast.error('Lesson not found');
        navigate('/academy/courses');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAudio = async () => {
    if (audioUrl) return;
    setAudioLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/academy/lesson/${lessonId}/audio`, { headers });
      const streamUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.audio_url}`;
      setAudioUrl(streamUrl);
      // Auto-play once loaded
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
        }
      }, 300);
    } catch (error) {
      toast.error('Failed to generate audio. Please try again.');
    } finally {
      setAudioLoading(false);
    }
  };

  const togglePlay = () => {
    if (!audioUrl) {
      loadAudio();
      return;
    }
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
      }
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleQuizSubmit = async () => {
    if (!lesson?.quiz) return;
    
    // Check all questions answered
    const questionIds = lesson.quiz.questions.map(q => q.id);
    const allAnswered = questionIds.every(id => quizAnswers[id] !== undefined);
    
    if (!allAnswered) {
      toast.error('Please answer all questions');
      return;
    }

    setQuizSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/academy/lesson/${lessonId}/quiz/submit`,
        { answers: quizAnswers },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setQuizResults(response.data);
      
      if (response.data.passed) {
        toast.success('Quiz passed! Lesson completed.');
      } else {
        toast.error('Quiz not passed. Review and try again.');
      }
    } catch (error) {
      toast.error('Failed to submit quiz');
    } finally {
      setQuizSubmitting(false);
    }
  };

  const resetQuiz = () => {
    setQuizAnswers({});
    setQuizResults(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!lesson) return null;

  const currentQuestionIndex = lesson.quiz?.questions 
    ? Object.keys(quizAnswers).length 
    : 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="academy-lesson-page">
      <Navbar />

      {/* Header */}
      <div className="pt-16 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate(`/academy/course/${lesson.course?.slug}`)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Course
              </Button>
              <div className="hidden sm:block h-6 w-px bg-slate-200" />
              <span className="hidden sm:block text-sm text-slate-600">
                {lesson.course?.title}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              {lesson.prev_lesson && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/academy/lesson/${lesson.prev_lesson.id}`)}
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1">Previous</span>
                </Button>
              )}
              {lesson.next_lesson && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/academy/lesson/${lesson.next_lesson.id}`)}
                >
                  <span className="hidden sm:inline mr-1">Next</span>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Lesson Title */}
            <div className="mb-6">
              <Badge variant="outline" className="mb-3">
                {lesson.module?.title}
              </Badge>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                {lesson.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {lesson.duration_minutes} min read
                </span>
                {lesson.completed && (
                  <Badge className="bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Completed
                  </Badge>
                )}
                <ShareButtons title={lesson.title} description={lesson.module?.title || ''} />
              </div>
            </div>

            {/* Audio Player */}
            <Card className="mb-6 bg-gradient-to-r from-primary/5 to-purple-50 border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-12 h-12 rounded-full"
                    onClick={togglePlay}
                    disabled={audioLoading}
                  >
                    {audioLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : isPlaying ? (
                      <Pause className="w-5 h-5" />
                    ) : (
                      <Play className="w-5 h-5" />
                    )}
                  </Button>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">Listen to this lesson</p>
                    <p className="text-sm text-slate-600">
                      {audioLoading ? 'Generating audio... this may take 15-30 seconds' :
                       audioUrl ? 'Audio ready — press play' : 'Click play to generate audio'}
                    </p>
                  </div>
                  {audioUrl && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={toggleMute}
                    >
                      {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </Button>
                  )}
                </div>
                {audioUrl && (
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    onEnded={() => setIsPlaying(false)}
                    className="hidden"
                  />
                )}
              </CardContent>
            </Card>

            {/* Lesson Content */}
            <Card className="mb-8">
              <CardContent className="p-6 md:p-8">
                <div className="prose prose-slate max-w-none prose-headings:font-heading prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-pre:relative">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      pre({ children }) {
                        return <>{children}</>;
                      },
                      code({ inline, className, children, ...props }) {
                        const text = String(children).replace(/\n$/, '');
                        if (inline) {
                          return <code className={className} {...props}>{children}</code>;
                        }
                        return (
                          <CopyBlock text={text} />
                        );
                      }
                    }}
                  >
                    {lesson.content}
                  </ReactMarkdown>
                </div>
              </CardContent>
            </Card>

            {/* Key Takeaways */}
            {lesson.key_takeaways && lesson.key_takeaways.length > 0 && (
              <Card className="mb-8 border-amber-200 bg-gradient-to-br from-amber-50/80 to-orange-50/40" data-testid="key-takeaways-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Lightbulb className="w-5 h-5 text-amber-600" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-slate-900">Key Takeaways</h3>
                  </div>
                  <ul className="space-y-3">
                    {lesson.key_takeaways.map((takeaway, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="mt-1.5 w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                        <span className="text-sm text-slate-700 leading-relaxed">{takeaway}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 pt-4 border-t border-amber-200/60">
                    <button
                      onClick={() => {
                        const text = `Key Takeaways from "${lesson.title}":\n\n${lesson.key_takeaways.map((t, i) => `${i + 1}. ${t}`).join('\n')}\n\nLearn more at AISkillFlow`;
                        navigator.clipboard.writeText(text);
                        toast.success('Takeaways copied — ready to share!');
                      }}
                      className="inline-flex items-center gap-2 text-xs font-medium text-amber-700 hover:text-amber-900 transition-colors"
                      data-testid="share-takeaways-btn"
                    >
                      <Share2 className="w-3.5 h-3.5" />
                      Copy & Share Takeaways
                    </button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quiz Section */}
            {lesson.quiz && (
              <Card className="border-primary/20" id="quiz">
                <CardHeader className="bg-primary/5 border-b border-primary/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                        <HelpCircle className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Lesson Quiz</CardTitle>
                        <p className="text-sm text-slate-600">
                          Pass with 70% to complete this lesson
                        </p>
                      </div>
                    </div>
                    {lesson.quiz_attempt?.passed && (
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Passed ({Math.round(lesson.quiz_attempt.score)}%)
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  {!showQuiz && !quizResults ? (
                    <div className="text-center py-4">
                      <p className="text-slate-600 mb-4">
                        Test your understanding of this lesson with a quick quiz.
                      </p>
                      <Button onClick={() => setShowQuiz(true)} data-testid="start-quiz-btn">
                        Start Quiz ({lesson.quiz.questions?.length || 0} questions)
                      </Button>
                    </div>
                  ) : quizResults ? (
                    <div className="space-y-6">
                      {/* Results Summary */}
                      <div className={`p-6 rounded-lg text-center ${
                        quizResults.passed ? 'bg-green-50' : 'bg-red-50'
                      }`}>
                        <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
                          quizResults.passed ? 'bg-green-100' : 'bg-red-100'
                        }`}>
                          {quizResults.passed ? (
                            <CheckCircle className="w-8 h-8 text-green-600" />
                          ) : (
                            <XCircle className="w-8 h-8 text-red-600" />
                          )}
                        </div>
                        <h3 className="text-xl font-semibold mb-2">
                          {quizResults.passed ? 'Quiz Passed!' : 'Not Quite'}
                        </h3>
                        <p className="text-3xl font-bold mb-2">
                          {Math.round(quizResults.score)}%
                        </p>
                        <p className="text-slate-600">
                          {quizResults.correct_count} of {quizResults.total_questions} correct
                        </p>
                      </div>

                      {/* Question Results */}
                      <div className="space-y-4">
                        {quizResults.results.map((result, idx) => {
                          const question = lesson.quiz.questions.find(q => q.id === result.question_id);
                          return (
                            <div key={result.question_id} className={`p-4 rounded-lg border ${
                              result.is_correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                            }`}>
                              <div className="flex items-start gap-3">
                                {result.is_correct ? (
                                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                                ) : (
                                  <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                                )}
                                <div>
                                  <p className="font-medium text-slate-900 mb-2">
                                    {question?.question}
                                  </p>
                                  {!result.is_correct && (
                                    <p className="text-sm text-green-700 mb-2">
                                      Correct answer: {question?.options[result.correct_answer]}
                                    </p>
                                  )}
                                  <p className="text-sm text-slate-600">
                                    {result.explanation}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-3 justify-center">
                        {!quizResults.passed && (
                          <Button onClick={resetQuiz} variant="outline">
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Try Again
                          </Button>
                        )}
                        {lesson.next_lesson && quizResults.passed && (
                          <Button onClick={() => navigate(`/academy/lesson/${lesson.next_lesson.id}`)}>
                            Next Lesson
                            <ChevronRight className="w-4 h-4 ml-2" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Progress */}
                      <div className="flex items-center justify-between text-sm text-slate-600">
                        <span>Question {currentQuestionIndex + 1} of {lesson.quiz.questions?.length}</span>
                        <Progress value={(currentQuestionIndex / lesson.quiz.questions?.length) * 100} className="w-32 h-2" />
                      </div>

                      {/* Questions */}
                      {lesson.quiz.questions?.map((question, idx) => (
                        <div key={question.id} className="p-4 bg-slate-50 rounded-lg">
                          <p className="font-medium text-slate-900 mb-4">
                            {idx + 1}. {question.question}
                          </p>
                          <RadioGroup
                            value={quizAnswers[question.id]?.toString()}
                            onValueChange={(value) => setQuizAnswers({
                              ...quizAnswers,
                              [question.id]: parseInt(value)
                            })}
                          >
                            {question.options?.map((option, optIdx) => (
                              <div key={optIdx} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white transition-colors">
                                <RadioGroupItem value={optIdx.toString()} id={`${question.id}-${optIdx}`} />
                                <Label htmlFor={`${question.id}-${optIdx}`} className="flex-1 cursor-pointer">
                                  {option}
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </div>
                      ))}

                      {/* Submit */}
                      <Button 
                        className="w-full" 
                        onClick={handleQuizSubmit}
                        disabled={quizSubmitting}
                        data-testid="submit-quiz-btn"
                      >
                        {quizSubmitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          'Submit Quiz'
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8">
              {lesson.prev_lesson ? (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/academy/lesson/${lesson.prev_lesson.id}`)}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous: {lesson.prev_lesson.title?.substring(0, 30)}...
                </Button>
              ) : <div />}
              
              {lesson.next_lesson && (
                <Button
                  onClick={() => navigate(`/academy/lesson/${lesson.next_lesson.id}`)}
                >
                  Next: {lesson.next_lesson.title?.substring(0, 30)}...
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Download Resources */}
            {lesson.checklist_text && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Resources
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                  <a 
                    href={`${API}/academy/lesson/${lessonId}/checklist`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="download-checklist"
                  >
                    <Button variant="outline" size="sm" className="w-full justify-start">
                      <Download className="w-4 h-4 mr-2" />
                      Lesson Checklist
                    </Button>
                  </a>
              </CardContent>
            </Card>
            )}

            {/* AI Coach CTA */}
            <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-indigo-50">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-gradient-to-br from-primary to-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <h4 className="font-semibold text-sm text-slate-900 mb-1">Need help with this lesson?</h4>
                <p className="text-xs text-slate-600 mb-3">Ask your AI Coach for explanations, examples, and advice</p>
                <Link to={`/academy/coach?lesson=${lessonId}`}>
                  <Button size="sm" className="w-full" data-testid="ask-ai-coach-btn">
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Ask AI Coach
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Course Progress */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Course Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <Link 
                  to={`/academy/course/${lesson.course?.slug}`}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <BookOpen className="w-5 h-5 text-primary" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {lesson.course?.title}
                    </p>
                    <p className="text-xs text-slate-500">
                      View full course
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcademyLesson;
